import React, { useEffect, useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import ActivityMetricsTable from './components/ActivityMetricsTable';
import {
  ActivityMetricsTableData,
  COLUMNS,
  HEADING_TRANSLATIONS,
  SORTING_VALUES,
  LOCKED_COLUMN,
} from './components/ActivityMetricsTable/data';
import api from 'api';
import usePageParams from 'hooks/usePageParams';
import useQueryParams from 'hooks/useQueryParams';
import useNumberOfResultsPerPage from 'hooks/useNumberOfResultsPerPage';
import useMultiSort from 'hooks/useMultiSort';
import MultiSortPanel from 'components/MultiSortPanel';
import NumberOfResultsPerPageFilter from 'components/NumberOfResultsPerPageFilter';
import mixpanelService from 'services/mixpanelService';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import { getMixpanelErrorProps } from 'axiosUtils';
import { ErrorViewedProps } from 'models/MixpanelEvents';
import { ASCENDING_DESCENDING_COPY, EARLIEST_LATEST_COPY } from 'models/Sort';
import useTableFetchState from 'hooks/useTableFetchState';
import useActivityMetricsFilterOptions, {
  HAS_LISTING_FILTER_KEY,
  LISTING_STATUS_FILTER_KEY,
} from './hooks/useActivityMetricsFilterOptions';
import MultiFilterPanel from 'components/MultiFilterPanel';
import ActiveFilterSortPills from 'components/ActiveFilterSortPills';
import useChipLabelClickRefs from 'hooks/useChipLabelClickRefs';
import {
  getFilterOptionByValue,
  getHasActiveFilters,
  getIsArrayEmpty,
} from 'utils';
import { ColumnFilterOption } from 'components/ReorderResizeTable/types';
import useTableColumnSettings from 'components/ReorderResizeTable/hooks/useTableColumnSettings';
import { TABLES_WITH_COLUMN_SETTINGS } from 'models/User';
import ManageColumns from 'components/ManageColumns';

import './ActivityMetrics.styles.scss';

const MULTI_SORT_DIRECTION_COPY: Record<
  string,
  Record<string, string> | undefined
> = {
  dealCreatedAt: EARLIEST_LATEST_COPY,
  latestLeadAt: EARLIEST_LATEST_COPY,
  latestNoteAt: EARLIEST_LATEST_COPY,
  latestActivityAt: EARLIEST_LATEST_COPY,
  messageChannelsCount: ASCENDING_DESCENDING_COPY,
  filesCount: ASCENDING_DESCENDING_COPY,
};

type ActivityMetricsProps = {
  className?: string;
};

const ActivityMetrics: React.FC<ActivityMetricsProps> = (props) => {
  const { className } = props;

  const {
    isLoading,
    totalPages,
    totalItems,
    data: activityMetrics,
    setIsLoading,
    setPaginatedData,
  } = useTableFetchState<ActivityMetricsTableData>();

  const classes = classNames('activity-metrics', className);

  const { t } = useTranslation();

  const getMixpanelProps = useGetMixpanelProps();

  const page = usePageParams();

  const { limit, updateLimit } = useNumberOfResultsPerPage();

  const { params } = useQueryParams();

  const sort = params?.sort;
  const hasListing = params?.[HAS_LISTING_FILTER_KEY];
  const listingStatus = params?.[LISTING_STATUS_FILTER_KEY];

  const { multiSortValues, multiSortDirectionTranslation } = useMultiSort(
    COLUMNS,
    SORTING_VALUES,
    HEADING_TRANSLATIONS,
    MULTI_SORT_DIRECTION_COPY,
  );

  const filterOptions = useActivityMetricsFilterOptions();

  const {
    columns,
    allColumns,
    hiddenColumns,
    columnSizing,
    isDefaultColumns,
    onReorder,
    onColumnSizingChange,
    saveColumnSettings,
    resetToDefault,
  } = useTableColumnSettings(TABLES_WITH_COLUMN_SETTINGS.DEAL_METRICS, COLUMNS);

  const {
    multiFilterRef,
    multiSortRef,
    onChipLabelClick,
    onSortChipLabelClick,
  } = useChipLabelClickRefs();

  const isEmpty = !isLoading && getIsArrayEmpty(activityMetrics);

  const hasActiveFilters = useMemo(
    () => getHasActiveFilters(filterOptions, params),
    [filterOptions, params],
  );

  const columnFilterOptions = useMemo(() => {
    const columnSortOptions = COLUMNS.reduce((acc, column) => {
      const sortValue = SORTING_VALUES[column];

      if (MULTI_SORT_DIRECTION_COPY[sortValue])
        acc[column] = {
          sortDirectionTranslation: multiSortDirectionTranslation[sortValue],
        };

      return acc;
    }, {} as Partial<Record<keyof ActivityMetricsTableData, ColumnFilterOption>>);

    return {
      ...columnSortOptions,
      hasListing: {
        ...columnSortOptions.hasListing,
        options: getFilterOptionByValue(filterOptions, HAS_LISTING_FILTER_KEY),
      },
      listingStatus: {
        ...columnSortOptions.listingStatus,
        options: getFilterOptionByValue(
          filterOptions,
          LISTING_STATUS_FILTER_KEY,
        ),
      },
    } as Partial<Record<keyof ActivityMetricsTableData, ColumnFilterOption>>;
  }, [filterOptions, multiSortDirectionTranslation]);

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const { data } = await api.portfolio.admin.getActivityMetrics({
          $page: page,
          $sort: sort,
          $limit: limit,
          ...(hasListing && { hasListing }),
          ...(listingStatus && { listingStatus }),
        });
        setPaginatedData(data);
      } catch (e) {
        mixpanelService.errorViewed(
          getMixpanelProps(getMixpanelErrorProps(e)) as ErrorViewedProps,
        );
      } finally {
        setIsLoading(false);
      }
    })();
  }, [
    limit,
    page,
    sort,
    hasListing,
    listingStatus,
    getMixpanelProps,
    setPaginatedData,
    setIsLoading,
  ]);

  return (
    <div className={classes}>
      <div className="admin-dashboard-router__heading">
        <h1>{t('dealMetrics')}</h1>

        <div className="activity-metrics__heading__right">
          {(!isEmpty || hasActiveFilters) && (
            <>
              {!isEmpty && (
                <NumberOfResultsPerPageFilter
                  limit={limit}
                  updateLimit={updateLimit}
                />
              )}
              <MultiFilterPanel ref={multiFilterRef} filters={filterOptions} />
              <MultiSortPanel
                ref={multiSortRef}
                options={multiSortValues}
                sortDirectionTranslations={multiSortDirectionTranslation}
              />
            </>
          )}
          <ManageColumns
            allColumns={allColumns}
            hiddenColumns={hiddenColumns}
            headingTranslations={HEADING_TRANSLATIONS}
            lockedColumn={LOCKED_COLUMN}
            isDefaultColumns={isDefaultColumns}
            onSave={saveColumnSettings}
            onResetToDefault={resetToDefault}
          />
        </div>
      </div>

      <ActiveFilterSortPills
        filterOptions={filterOptions}
        sortOptions={multiSortValues}
        onChipLabelClick={onChipLabelClick}
        onSortChipLabelClick={onSortChipLabelClick}
      />

      <ActivityMetricsTable
        isLoading={isLoading}
        totalPages={totalPages}
        totalItems={totalItems}
        data={activityMetrics}
        columnFilterOptions={columnFilterOptions}
        columns={columns}
        columnSizing={columnSizing}
        onReorder={onReorder}
        onColumnSizingChange={onColumnSizingChange}
      />
    </div>
  );
};

export default ActivityMetrics;
